import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vehicleService } from '../services/vehicleService';
import { generateVehicleSlug } from '../utils/vehicleSlug';
import { FaCar, FaTachometerAlt, FaArrowRight } from 'react-icons/fa';

/**
 * RelatedInventory — Drop-in component that fetches and renders
 * crawlable <Link> cards to vehicle detail pages.
 *
 * Props:
 *   make      — filter by make (e.g. "BMW", "Ford"). Optional.
 *   type      — filter by vehicle_type (e.g. "golf-cart", "trailer"). Optional.
 *   limit     — max vehicles to show (default 4)
 *   title     — section heading text (default auto-generated)
 *   linkAll   — path for "View all" link (default "/vehicles")
 *   excludeId — vehicle ID to exclude (useful on VDP pages)
 */
const RelatedInventory = ({
  make = null,
  type = null,
  limit = 4,
  title = null,
  linkAll = '/vehicles',
  excludeId = null
}) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVehicles();
  }, [make, type]);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      let result;
      if (type) {
        result = await vehicleService.getAvailableVehiclesByType(
          Array.isArray(type) ? type : [type]
        );
      } else {
        result = await vehicleService.getAvailableVehicles();
      }

      if (!result.error && result.data) {
        let filtered = result.data;

        // Filter by make if specified
        if (make) {
          const makeLower = make.toLowerCase();
          filtered = filtered.filter(v =>
            (v.make || '').toLowerCase() === makeLower
          );
        }

        // Exclude specific vehicle
        if (excludeId) {
          filtered = filtered.filter(v => v.id !== excludeId);
        }

        // Shuffle for variety, then take limit
        const shuffled = filtered.sort(() => 0.5 - Math.random());
        setVehicles(shuffled.slice(0, limit));
      }
    } catch (err) {
      console.error('RelatedInventory load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || vehicles.length === 0) return null;

  const sectionTitle = title || (make
    ? `${make} Vehicles in Stock`
    : type
      ? `Available ${type === 'golf-cart' ? 'Golf Carts' : type === 'trailer' ? 'Trailers' : 'Vehicles'}`
      : 'Featured Inventory');

  return (
    <section className="mt-12 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{sectionTitle}</h2>
        <Link
          to={linkAll}
          className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
        >
          View all inventory <FaArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {vehicles.map(vehicle => {
          const slug = generateVehicleSlug(vehicle);
          const rawImg = vehicle.images?.[0];
          const mainImage = (typeof rawImg === 'string' ? rawImg : rawImg?.url) || '/assets/placeholder-vehicle.jpg';
          return (
            <Link
              key={vehicle.id}
              to={`/vehicle/${slug}`}
              className="group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src={mainImage}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} for sale at Paradise-Performance`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  width="400"
                  height="300"
                />
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight group-hover:text-primary-600 transition-colors">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg font-bold text-gray-900">
                    {vehicle.price ? `$${vehicle.price.toLocaleString()}` : 'Call'}
                  </span>
                  {vehicle.mileage && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <FaTachometerAlt className="w-3 h-3" />
                      {vehicle.mileage.toLocaleString()} mi
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default RelatedInventory;
